import { PropsWithChildren } from 'react';

import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCol,
  IonGrid,
  IonLabel,
  IonRow,
  IonSkeletonText,
  IonToggle
} from '@ionic/react';
import { CheckoutEventsItem, CheckoutEventsTotals } from '@paddle/paddle-js';
import cs from 'classnames';

import styles from './PaymentCard.module.scss';

import {
  PADDLE_ENTERPRISE_PRODUCT_ANNUAL,
  PADDLE_ENTERPRISE_PRODUCT_ANNUAL_ADDITIONAL,
  PADDLE_ENTERPRISE_PRODUCT_MONTHLY,
  PADDLE_ENTERPRISE_PRODUCT_MONTHLY_ADDITIONAL,
  PADDLE_PERSONAL_PRODUCT_ANNUAL,
  PADDLE_PERSONAL_PRODUCT_MONTHLY,
  PADDLE_TEAM_PRODUCT_ANNUAL,
  PADDLE_TEAM_PRODUCT_ANNUAL_ADDITIONAL,
  PADDLE_TEAM_PRODUCT_MONTHLY,
  PADDLE_TEAM_PRODUCT_MONTHLY_ADDITIONAL
} from '../../constants/secrets';
import {
  PaddleProducts,
  Plan,
  PricingBillingPeriod
} from '../../types/pricing.types';
import { formatPrice } from '../../util/price';

export interface PaymentCardProps extends PropsWithChildren {
  billed: PricingBillingPeriod;
  checkoutComplete: boolean;
  currencyCode?: string;
  currencySymbol?: string;
  items: CheckoutEventsItem[];
  paddleLoaded: boolean;
  products: PaddleProducts;
  selectedPlan?: Plan;
  toggleBillingPeriod: () => void;
  totals?: CheckoutEventsTotals;
}

export const PaymentCard = ({
  selectedPlan,
  billed,
  paddleLoaded,
  checkoutComplete,
  items,
  totals,
  currencyCode,
  currencySymbol,
  children,
  products,
  toggleBillingPeriod
}: PaymentCardProps) => {
  const getPaymentCalculationText = (
    priceId: string,
    total: number,
    currency: string,
    quantity: number = 1
  ): string => {
    const memberText = quantity === 1 ? 'member' : 'members';
    if (priceId === PADDLE_PERSONAL_PRODUCT_MONTHLY) {
      return `${formatPrice(total, currency, 0)} per month`;
    } else if (priceId === PADDLE_PERSONAL_PRODUCT_ANNUAL) {
      return `${formatPrice(total / 12, currency, 0)} x 12 months`;
    } else if (priceId === PADDLE_TEAM_PRODUCT_MONTHLY) {
      return `${formatPrice(total, currency, 0)} per month (includes first 5 members)`;
    } else if (priceId === PADDLE_TEAM_PRODUCT_ANNUAL) {
      return `${formatPrice(total / 12, currency, 0)} x 12 months (includes first 5 members)`;
    } else if (priceId === PADDLE_TEAM_PRODUCT_MONTHLY_ADDITIONAL) {
      return `${formatPrice(total / Math.max(1, quantity), currency, 0)} per month x ${quantity} ${memberText}`;
    } else if (priceId === PADDLE_TEAM_PRODUCT_ANNUAL_ADDITIONAL) {
      return `${formatPrice(total / (12 * Math.max(1, quantity)), currency, 0)} x 12 months x ${quantity} ${memberText}`;
    } else if (priceId === PADDLE_ENTERPRISE_PRODUCT_MONTHLY) {
      return `${formatPrice(total, currency, 0)} per month (includes first 5 members)`;
    } else if (priceId === PADDLE_ENTERPRISE_PRODUCT_ANNUAL) {
      return `${formatPrice(total / 12, currency, 0)} x 12 months (includes first 5 members)`;
    } else if (priceId === PADDLE_ENTERPRISE_PRODUCT_MONTHLY_ADDITIONAL) {
      return `${formatPrice(total / Math.max(1, quantity), currency, 0)} per month x ${quantity} ${memberText}`;
    } else if (priceId === PADDLE_ENTERPRISE_PRODUCT_ANNUAL_ADDITIONAL) {
      return `${formatPrice(total / (12 * Math.max(1, quantity)), currency, 0)} x 12 months x ${quantity} ${memberText}`;
    } else {
      return 'Unknown price';
    }
  };

  const isTeamOrEnterprise =
    selectedPlan === Plan.Team || selectedPlan === Plan.Enterprise;

  const cardContentClass = cs({
    [styles.completed]: checkoutComplete
  });

  return (
    <div className={styles.cardContainer}>
      <IonCard className={styles.card}>
        {checkoutComplete && (
          <IonCardHeader>
            <IonCardTitle>Payment Successful!</IonCardTitle>
            <IonCardSubtitle>
              You have successfully subscribed to a {selectedPlan} plan, you
              will receive a confirmation email shortly.
            </IonCardSubtitle>
          </IonCardHeader>
        )}

        <IonCardContent className={cardContentClass}>
          <IonGrid>
            <IonRow>
              <IonCol>
                {children}
                <div
                  className={cs({
                    [styles.hidePaddle]: paddleLoaded
                  })}
                >
                  <IonLabel>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: '200px', height: '24px' }}
                    ></IonSkeletonText>
                  </IonLabel>
                  <IonLabel>
                    <IonSkeletonText
                      animated={true}
                      style={{
                        width: '300px',
                        height: '16px',
                        marginTop: '16px'
                      }}
                    ></IonSkeletonText>
                  </IonLabel>
                  <IonLabel>
                    <IonSkeletonText
                      animated={true}
                      style={{
                        width: '150px',
                        height: '16px',
                        marginTop: '8px'
                      }}
                    ></IonSkeletonText>
                  </IonLabel>

                  <IonLabel>
                    <IonSkeletonText
                      animated={true}
                      style={{
                        width: '150px',
                        height: '16px',
                        marginTop: '32px'
                      }}
                    ></IonSkeletonText>
                  </IonLabel>
                  <IonLabel>
                    <IonSkeletonText
                      animated={true}
                      style={{
                        width: '100%',
                        height: '36px',
                        marginTop: '16px'
                      }}
                    ></IonSkeletonText>
                  </IonLabel>

                  <IonLabel>
                    <IonSkeletonText
                      animated={true}
                      style={{
                        width: '150px',
                        height: '16px',
                        marginTop: '32px'
                      }}
                    ></IonSkeletonText>
                  </IonLabel>
                  <IonLabel>
                    <IonSkeletonText
                      animated={true}
                      style={{
                        width: '100%',
                        height: '36px',
                        marginTop: '16px'
                      }}
                    ></IonSkeletonText>
                  </IonLabel>

                  <IonLabel>
                    <IonSkeletonText
                      animated={true}
                      style={{
                        width: '150px',
                        height: '16px',
                        marginTop: '32px'
                      }}
                    ></IonSkeletonText>
                  </IonLabel>
                  <IonLabel>
                    <IonSkeletonText
                      animated={true}
                      style={{
                        width: '100%',
                        height: '36px',
                        marginTop: '16px'
                      }}
                    ></IonSkeletonText>
                  </IonLabel>

                  <IonLabel>
                    <IonSkeletonText
                      animated={true}
                      style={{
                        width: '100%',
                        height: '30px',
                        marginTop: '32px'
                      }}
                    ></IonSkeletonText>
                  </IonLabel>

                  <IonLabel>
                    <IonSkeletonText
                      animated={true}
                      style={{
                        width: '100%',
                        height: '36px',
                        marginTop: '32px'
                      }}
                    ></IonSkeletonText>
                  </IonLabel>
                </div>
              </IonCol>
              {!checkoutComplete && totals && currencySymbol && currencyCode ? (
                <IonCol
                  className={cs({
                    [styles.team]: isTeamOrEnterprise,
                    [styles.personal]: selectedPlan === Plan.Personal
                  })}
                >
                  <section
                    className={cs({
                      [styles.productContainer]: true,
                      [styles.team]: isTeamOrEnterprise,
                      [styles.personal]: selectedPlan === Plan.Personal
                    })}
                  >
                    {isTeamOrEnterprise ? (
                      <hgroup className={styles.productHeader}>
                        <div className={styles.productImage}>
                          <img
                            src="/img/team.jpg"
                            alt={
                              selectedPlan === Plan.Enterprise
                                ? 'Enterprise plan image'
                                : 'Team plan image'
                            }
                          />
                        </div>
                        <h2 className={styles.productTitle}>
                          {selectedPlan === Plan.Team ? 'Team' : 'Enterprise'}
                          <div className={styles.productBilled}>
                            <IonToggle
                              color="light"
                              checked={billed === PricingBillingPeriod.Annually}
                              onClick={() => toggleBillingPeriod()}
                            ></IonToggle>
                            <IonLabel>Billed {billed}</IonLabel>
                          </div>
                        </h2>
                        <div className={styles.productDescription}>
                          <span>Unlock all features for your whole team.</span>
                          <span>
                            Automated billing ensures you only get billed for
                            active members.
                          </span>
                          <span>Cancel anytime.</span>
                        </div>
                      </hgroup>
                    ) : (
                      <hgroup className={styles.productHeader}>
                        <div className={styles.productImage}>
                          <img
                            src="/img/personal.jpg"
                            alt="Personal Plan Image"
                          />
                        </div>
                        <h2 className={styles.productTitle}>
                          Personal
                          <div className={styles.productBilled}>
                            <IonToggle
                              color="light"
                              checked={billed === PricingBillingPeriod.Annually}
                              onClick={() => toggleBillingPeriod()}
                            ></IonToggle>
                            <IonLabel>Billed {billed}</IonLabel>
                          </div>
                        </h2>
                        <div className={styles.productDescription}>
                          <span>
                            Unlock all features for just yourself with any team
                            you work with.
                          </span>
                          <span>Cancel anytime.</span>
                        </div>
                      </hgroup>
                    )}

                    {paddleLoaded && (
                      <section className={styles.pricing}>
                        {items?.map((item) => (
                          <div key={item.price_id}>
                            <div className={styles.row}>
                              <span className={cs([styles.bold, styles.large])}>
                                {item.product.name}
                              </span>
                              <span className={cs([styles.bold, styles.large])}>
                                {formatPrice(
                                  item.totals.subtotal,
                                  currencySymbol
                                )}
                              </span>
                            </div>
                            <div className={styles.row}>
                              <span className={styles.normal}>
                                {getPaymentCalculationText(
                                  item.price_id,
                                  item.totals.subtotal,
                                  currencySymbol,
                                  item.quantity
                                )}
                              </span>
                            </div>
                          </div>
                        ))}
                        {isTeamOrEnterprise && items.length === 1 && (
                          <div>
                            <div className={styles.row}>
                              <span className={cs([styles.bold, styles.large])}>
                                Additional Members
                              </span>
                              <span className={cs([styles.bold, styles.large])}>
                                {formatPrice(0, currencySymbol)}
                              </span>
                            </div>
                            <div className={styles.row}>
                              <span className={styles.normal}>
                                {getPaymentCalculationText(
                                  selectedPlan === Plan.Team
                                    ? billed === PricingBillingPeriod.Annually
                                      ? PADDLE_TEAM_PRODUCT_ANNUAL_ADDITIONAL
                                      : PADDLE_TEAM_PRODUCT_MONTHLY_ADDITIONAL
                                    : billed === PricingBillingPeriod.Annually
                                      ? PADDLE_ENTERPRISE_PRODUCT_ANNUAL_ADDITIONAL
                                      : PADDLE_ENTERPRISE_PRODUCT_MONTHLY_ADDITIONAL,
                                  selectedPlan === Plan.Team
                                    ? billed === PricingBillingPeriod.Annually
                                      ? products[Plan.Team].prices
                                          .annualAdditional.price
                                      : products[Plan.Team].prices
                                          .monthlyAdditional.price
                                    : billed === PricingBillingPeriod.Annually
                                      ? products[Plan.Enterprise].prices
                                          .annualAdditional.price
                                      : products[Plan.Enterprise].prices
                                          .monthlyAdditional.price,
                                  currencySymbol,
                                  0
                                )}
                              </span>
                            </div>
                          </div>
                        )}

                        <hr />

                        <div className={styles.row}>
                          <span className={styles.normal}>Subtotal</span>
                          <span className={cs([styles.bold, styles.normal])}>
                            {formatPrice(totals?.subtotal, currencySymbol)}
                          </span>
                        </div>
                        {totals.discount > 0 && (
                          <div className={styles.row}>
                            <span className={styles.normal}>
                              Discount: {totals.discount}
                            </span>
                            <span
                              className={cs([
                                styles.bold,
                                styles.normal,
                                styles.discount
                              ])}
                            >
                              -{formatPrice(totals?.discount, currencySymbol)}
                            </span>
                          </div>
                        )}
                        <div className={styles.row}>
                          <span className={styles.normal}>Tax</span>
                          <span className={cs([styles.bold, styles.normal])}>
                            {formatPrice(totals?.tax, currencySymbol)}
                          </span>
                        </div>

                        <hr />

                        <div className={styles.row}>
                          <span className={styles.normal}>Total</span>
                          <span className={cs([styles.bold, styles.large])}>
                            {formatPrice(totals?.total, currencyCode)}
                          </span>
                        </div>
                      </section>
                    )}
                  </section>
                </IonCol>
              ) : (
                <IonCol>
                  <section className={styles.productContainer}>
                    <section className={styles.pricing}>
                      <div className={styles.row}>
                        <IonSkeletonText
                          animated={true}
                          style={{ width: '100%', height: '150px' }}
                        ></IonSkeletonText>
                      </div>

                      <hr />

                      <div className={styles.row}>
                        <IonSkeletonText
                          animated={true}
                          style={{ width: '60%', height: '20px' }}
                        ></IonSkeletonText>
                      </div>

                      <hr />

                      <div className={styles.row}>
                        <IonSkeletonText
                          animated={true}
                          style={{ width: '100%', height: '14px' }}
                        ></IonSkeletonText>
                      </div>
                      <div className={styles.row}>
                        <IonSkeletonText
                          animated={true}
                          style={{ width: '60%', height: '14px' }}
                        ></IonSkeletonText>
                      </div>
                      <div className={styles.row}>
                        <IonSkeletonText
                          animated={true}
                          style={{ width: '80%', height: '14px' }}
                        ></IonSkeletonText>
                      </div>

                      <hr />

                      <div className={styles.row}>
                        <IonSkeletonText
                          animated={true}
                          style={{ width: '100%', height: '18px' }}
                        ></IonSkeletonText>
                      </div>
                    </section>

                    <section className={styles.pricing}>
                      <div className={styles.row}>
                        <IonSkeletonText
                          animated={true}
                          style={{ width: '100%', height: '18px' }}
                        ></IonSkeletonText>
                      </div>
                      <div className={styles.row}>
                        <IonSkeletonText
                          animated={true}
                          style={{ width: '100%', height: '14px' }}
                        ></IonSkeletonText>
                      </div>

                      <hr />

                      <div className={styles.row}>
                        <IonSkeletonText
                          animated={true}
                          style={{ width: '100%', height: '14px' }}
                        ></IonSkeletonText>
                      </div>
                      <div className={styles.row}>
                        <IonSkeletonText
                          animated={true}
                          style={{ width: '100%', height: '14px' }}
                        ></IonSkeletonText>
                      </div>

                      <hr />

                      <div className={styles.row}>
                        <IonSkeletonText
                          animated={true}
                          style={{ width: '100%', height: '18px' }}
                        ></IonSkeletonText>
                      </div>
                    </section>
                  </section>
                </IonCol>
              )}
            </IonRow>
          </IonGrid>
        </IonCardContent>
      </IonCard>
    </div>
  );
};
